import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import useAsyncEffect from 'use-async-effect';
import moment from 'moment-timezone';
import { FiArrowLeft, FiFileText, FiEye, FiBarChart2, FiCheckCircle, FiUsers, FiClock, FiInfo, FiLoader } from 'react-icons/fi';
import { CampaignDetailsInfo, GetCampaignResult } from '../../../model/GetCampaignsResult';
import api from '../../../api/api';
import { pluralize, to, plainText } from '../../../util/util';
import CampaignStatistics from './CampaignStatistics';
import useSwitchToClient from '../useSwitchToClient';

const TZ = 'Australia/Sydney';

const suppressionTip = 'Our email provider won’t send to these addresses because an earlier email to them bounced, or the person marked one as spam. They’re skipped automatically to protect your sender reputation.';

export type CampaignView = 'details' | 'preview' | 'statistics';
type EmailView = 'Desktop' | 'Mobile' | 'Plain Text';

// Details spec 2:59898, Email preview spec 2:60059, Statistics spec 2:60217 (CampaignStatistics). Omitted: Campaign name (the subject is the label), Export Report, Duplicate.
const CampaignDetails: React.FC<{ campaignId: string; view: CampaignView }> = ({ campaignId, view }) => {
    const history = useHistory();
    const [d, setD] = useState<CampaignDetailsInfo | null>(null);
    const [error, setError] = useState('');
    const [email, setEmail] = useState<GetCampaignResult | null>(null);
    const [emailError, setEmailError] = useState('');
    const [emailView, setEmailView] = useState<EmailView>('Desktop');
    const [frameHeight, setFrameHeight] = useState(900);

    useAsyncEffect(async isMounted => {
        const [err, data] = await to(api().getCampaignDetails(campaignId));
        if (!isMounted()) return;
        if (err || !data) { setError(err?.response?.status === 403 ? 'You don’t have access to this campaign.' : err?.message ?? 'Loading failed'); return; }
        setD(data);
    }, [campaignId]);
    useSwitchToClient(d?.stockId);

    // the stored campaign html, exactly as it was queued
    useAsyncEffect(async isMounted => {
        if (view !== 'preview' || email) return;
        const [err, data] = await to(api().getCampaign(campaignId));
        if (!isMounted()) return;
        if (err || !data) { setEmailError(err?.message ?? 'Loading the email failed'); return; }
        setEmail(data);
    }, [campaignId, view]);

    const back = <button type="button" className="link back" onClick={() => history.push('/communications/email-campaigns')}><FiArrowLeft /> Back to Email campaigns</button>;
    if (error) return <div className="announcement-preview campaign-details">{back}<p className="error">{error}</p></div>;
    if (!d) return <div className="announcement-preview campaign-details">{back}<p className="loading">Loading campaign…</p></div>;

    const sentAt = d.sentUtc ? moment(d.sentUtc).tz(TZ) : null;
    const listText = d.lists.length ? ` on the ${d.lists.join(' and ')} ${d.lists.length > 1 ? 'lists' : 'list'}` : '';
    const toText = `${pluralize('contact', d.recipients)}${listText}`;
    const bounced = d.suppressed + d.rejected;
    const from = d.fromName && d.fromEmail ? `${d.fromName} <${d.fromEmail}>` : d.fromName || d.fromEmail || '—';

    const field = (label: string, value: React.ReactNode, strong = false) =>
        <div className="field"><dt>{label}</dt><dd className={strong ? 'strong' : ''}>{value}</dd></div>;

    const base = `/communications/campaign/${encodeURIComponent(campaignId)}`;
    const navItem = (icon: React.ReactNode, title: string, sub: string, active: boolean, to?: string) =>
        <button type="button" className={`nav-item${active ? ' active' : ''}`} disabled={!active && !to} title={active || to ? undefined : 'Coming soon'}
            onClick={() => to && !active && history.push(to)}>
            <span className="icon">{icon}</span>
            <span className="t"><strong>{title}</strong><span>{sub}</span></span>
        </button>;

    const fitFrame = (e: React.SyntheticEvent<HTMLIFrameElement>) => {
        const body = e.currentTarget.contentDocument?.body;
        if (body) setFrameHeight(body.scrollHeight + 24);
    };

    const stage = () => {
        if (emailError) return <p className="error">{emailError}</p>;
        if (!email) return <p className="loading"><FiLoader className="spin" /> Loading the email…</p>;
        if (!email.html) return <p className="loading">No copy of this email was stored.</p>;
        return <>
            <div className="envelope">
                <div className="from">From: {from}</div>
                <div className="subject">Subject: {email.subject}</div>
            </div>
            {emailView === 'Plain Text'
                ? <pre className="plain">{plainText(email.html)}</pre>
                : <iframe title="Email preview" className={emailView === 'Mobile' ? 'mobile' : ''} srcDoc={email.html} sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                    style={{ height: frameHeight }} onLoad={fitFrame} />}
        </>;
    };

    return <div className="announcement-preview campaign-details">
        {back}
        <div className="title-row">
            <div>
                <h1>{d.subject}</h1>
                <div className="meta">
                    <span className={`pill ${sentAt ? 'ok' : 'warn'}`}>{d.status}</span>
                    <span>{sentAt ? `Sent ${sentAt.format('D MMM YYYY, h:mm a')} Sydney · ` : ''}{pluralize('recipient', d.recipients)}</span>
                </div>
            </div>
        </div>

        <div className="layout">
            <div className="card side-nav">
                <div className="label">This campaign</div>
                {navItem(<FiFileText />, 'Campaign Details', 'What it was and who got it', view === 'details', `${base}/details`)}
                {navItem(<FiEye />, 'Email Preview', 'Exactly what was sent', view === 'preview', `${base}/preview`)}
                {navItem(<FiBarChart2 />, 'Statistics', 'Opens, clicks, who read it', view === 'statistics', `${base}/statistics`)}
                <div className="email-status">
                    <div className="label">Status</div>
                    {sentAt
                        ? <div className="status ok"><FiCheckCircle /> Sent {sentAt.format('D MMM, h:mm a')}</div>
                        : <div className="status warn"><FiClock /> {d.status} — sending</div>}
                    <div className="to">To {toText}</div>
                </div>
            </div>

            {view === 'statistics' && <CampaignStatistics campaignId={campaignId} />}

            {view === 'preview' && <div className="main">
                <div className="card views">
                    {(['Desktop', 'Mobile', 'Plain Text'] as EmailView[]).map(v =>
                        <button key={v} type="button" className={v === emailView ? 'active' : ''} onClick={() => setEmailView(v)}>{v}</button>)}
                    <span className="record-note">{sentAt ? `Sent ${sentAt.format('D MMMM')}` : 'Sending'} — this is a record, so it can’t be edited</span>
                </div>
                <div className="stage">{stage()}</div>
            </div>}

            {view === 'details' && <div className="main announcement-details">
                <div className="card">
                    <div className="card-head">
                        <span className="icon-tile"><FiCheckCircle /></span>
                        <div>
                            <h2>{sentAt ? 'This Campaign Has Been Sent' : 'This Campaign Is Sending'}</h2>
                            <p className="sub">Details are locked once a campaign goes out, so what you see is what investors received</p>
                        </div>
                    </div>
                    <dl>
                        {field('Subject line', d.subject, true)}
                        {field('Preview line', d.previewLine || '—')}
                        {field('From', from)}
                        {field('Sent', sentAt ? `${sentAt.format('D MMMM YYYY, h:mm a')} Sydney` : '—', true)}
                        {field('Sent by', d.sentBy || '—')}
                        {field('Template', d.templateName || '—')}
                    </dl>
                </div>

                <div className="card">
                    <div className="card-head">
                        <span className="icon-tile"><FiUsers /></span>
                        <div>
                            <h2>Who Received It</h2>
                            <p className="sub">{toText}</p>
                        </div>
                    </div>
                    <dl>
                        {field('Delivered', d.delivered.toLocaleString())}
                        {field('Bounced', <>
                            {bounced.toLocaleString()}
                            {bounced > 0 && <> — {d.suppressed.toLocaleString()} on the suppression list <span className="tip" title={suppressionTip}><FiInfo /></span> · {d.rejected.toLocaleString()} rejected by the mail server</>}
                        </>)}
                        {field('Unsubscribed from this email', d.unsubscribed.toLocaleString())}
                    </dl>
                </div>
            </div>}
        </div>
    </div>;
};

export default CampaignDetails;
